/* eslint-disable react-hooks/set-state-in-effect */
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import Animate, { AnimationTypes } from "../../components/Animate/Animate"
import ModalPortal from "../../components/Modal/ModalPortal"
import { TitlePageResponsiveIconSmall } from "../../components/Titles/Titles"
import { faClose, faSackDollar, faUsers, faUserTie } from "@fortawesome/free-solid-svg-icons"
import Button, { ButtonIcon } from "../../components/Button/Button"
import { GenericDropDown } from "../../components/Dropdown/Dropdown"
import { MetricCard } from "../../components/MetricCard/MetricCard"
import THEME from "../../configuration/Theme"
import { SIZE } from "../../configuration/Sizes"
import defaultStyle from "../../styles/defaultPageStyle.module.css"
import { convertToLocalMoney } from "../../Objects/Generics/Helps"
import { EEnableStatusType } from "../../Enum/EEnableStatusType"
import { ResellerCommissionPlan } from "../../Objects/AdminReseller/ResellerCommissionPlan"
import { useAdminReseller, AdminResellerDetail } from "../../hooks/AdminDashboard/useAdminReseller"
import { ResellerClientsPopup } from "./ResellerClientsPopup"
import { ResellerCommissionStatementPopup } from "./ResellerCommissionStatementPopup"
import { ResellerCouponsPopup } from "./ResellerCouponsPopup"
import GetSpace from "../../utils/space"

interface AdminResellerDetailsPopupProps {
  resellerId: number
  onClose: () => void
}

export const AdminResellerDetailsPopup: React.FC<AdminResellerDetailsPopupProps> = ({ resellerId, onClose }) => {
  const { t } = useTranslation("admin")

  const {
    handleGetResellerDetail,
    handleMarkCommissionEntryAsPaid,
    handleCreateCommissionPayment,
    handleGetAllCommissionPlans,
    handleUpdateResellerStatus,
    handleAssignCommissionPlan,
  } = useAdminReseller()

  const [detail, setDetail] = useState<AdminResellerDetail | null>(null)

  const [plans, setPlans] = useState<ResellerCommissionPlan[]>([])

  const [status, setStatus] = useState<EEnableStatusType>(EEnableStatusType.Active)

  const [commissionPlanEntityId, setCommissionPlanEntityId] = useState<number | null>(null)

  const [isClientsPopupOpen, setIsClientsPopupOpen] = useState(false)

  const [isCommissionsPopupOpen, setIsCommissionsPopupOpen] = useState(false)

  const [isCouponsPopupOpen, setIsCouponsPopupOpen] = useState(false)

  const refresh = () => handleGetResellerDetail(resellerId, setDetail)

  useEffect(() => {
    refresh()
    handleGetAllCommissionPlans(setPlans)
  }, [resellerId])

  useEffect(() => {
    if (!detail) return

    setStatus(detail.reseller.status)

    const existingCommissionPlanEntityId = detail.reseller.currentCommissionPlanEntityId

    if (existingCommissionPlanEntityId) {
      setCommissionPlanEntityId(existingCommissionPlanEntityId)

      return
    }

    setCommissionPlanEntityId(plans[0]?.id ?? null)
  }, [detail, plans])

  const handleMarkPaid = (commissionEntryId: number) => {
    const reference = window.prompt(t("reseller_mark_paid_reference_prompt")) ?? undefined

    handleMarkCommissionEntryAsPaid(commissionEntryId, reference, refresh)
  }

  const handleCreatePayment = () => {
    const reference = window.prompt(t("reseller_create_payment_reference_prompt")) ?? undefined

    handleCreateCommissionPayment(resellerId, reference, refresh)
  }

  const handleSave = (event: React.FormEvent) => {
    event.preventDefault()

    handleUpdateResellerStatus(resellerId, status, () => {
      if (commissionPlanEntityId) {
        handleAssignCommissionPlan(resellerId, commissionPlanEntityId, () => {
          refresh()
          onClose()
        })

        return
      }

      refresh()
      onClose()
    })
  }

  const planOptions = plans.map((plan) => ({ label: plan.name, value: String(plan.id) }))

  return (
    <>
      <ModalPortal>
        <div className={defaultStyle.overlay}>
          <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
            <div className={`${defaultStyle.alert} max-h-[85vh] overflow-y-auto`}>
              <TitlePageResponsiveIconSmall
                title={detail?.reseller.name ?? t("reseller_details_title")}
                icon={faUserTie}
                iconColor={THEME.colors.iconBGColor}
                children={
                  <>
                    <GenericDropDown
                      data={Object.mapEnumToStrings(EEnableStatusType, { exclude: [EEnableStatusType.All] })}
                      initialState={status}
                      handlerFuncion={(e) => setStatus(e.target.selectedIndex as EEnableStatusType)}
                      descriptionKey="interactionDescriptions:pages.adminDashboard.adminResellerDetailsPopup.select.status"
                    />

                    <GetSpace left={5} />

                    <ButtonIcon
                      sizeNumber={SIZE.buttonCloseIconSize}
                      bgColor={THEME.colors.buttonBGCloseColor}
                      iconColor={THEME.colors.buttonFGCloseColor}
                      icon={faClose}
                      onClick={onClose}
                      descriptionKey="interactionDescriptions:pages.adminDashboard.adminResellerDetailsPopup.button.onClose"
                    />
                  </>
                }
              />

              {detail && (
                <div className="p-2">
                  <div className="mt-3 grid grid-cols-2 gap-3">
                    <MetricCard
                      title={t("reseller_column_active_clients")}
                      value={`${detail.reseller.activeClients}/${detail.reseller.totalClients}`}
                      icon={faUsers}
                    />

                    <MetricCard
                      title={t("reseller_column_commission_accrued")}
                      value={convertToLocalMoney(detail.reseller.commissionAccrued)}
                      icon={faSackDollar}
                    />

                    <MetricCard
                      title={t("reseller_column_commission_paid")}
                      value={convertToLocalMoney(detail.reseller.commissionPaid)}
                      icon={faSackDollar}
                      color="#16a34a"
                    />

                    <MetricCard
                      title={t("reseller_column_commission_pending")}
                      value={convertToLocalMoney(detail.reseller.commissionPending)}
                      icon={faSackDollar}
                      color="#d97706"
                    />
                  </div>

                  <div className={defaultStyle.divisionBar}></div>

                  <form onSubmit={handleSave}>
                    {planOptions.length > 0 ? (
                      <GenericDropDown
                        legend={t("reseller_column_commission_plan")}
                        data={planOptions}
                        initialState={commissionPlanEntityId ? String(commissionPlanEntityId) : ""}
                        handlerFuncion={(e) => setCommissionPlanEntityId(Number(e.target.value))}
                        descriptionKey="interactionDescriptions:pages.adminDashboard.adminResellerDetailsPopup.select.commissionPlan"
                      />
                    ) : (
                      <p className="p-2 text-sm text-gray-500">{t("reseller_commission_plans_empty")}</p>
                    )}

                    <div className={defaultStyle.divisionBar}></div>

                    <div className={defaultStyle.buttonsController}>
                      <Button
                        typeButton="button"
                        label={t("reseller_view_clients_action")}
                        bgColor={THEME.colors.buttonBGRegisterColor}
                        onClick={() => setIsClientsPopupOpen(true)}
                        descriptionKey="interactionDescriptions:pages.adminDashboard.adminResellerDetailsPopup.button.viewClients"
                      />

                      <Button
                        typeButton="button"
                        label={t("reseller_view_commissions_action")}
                        bgColor={THEME.colors.buttonBGRegisterColor}
                        onClick={() => setIsCommissionsPopupOpen(true)}
                        descriptionKey="interactionDescriptions:pages.adminDashboard.adminResellerDetailsPopup.button.viewCommissions"
                      />

                      <Button
                        typeButton="button"
                        label={t("reseller_view_coupons_action")}
                        bgColor={THEME.colors.buttonBGRegisterColor}
                        onClick={() => setIsCouponsPopupOpen(true)}
                        descriptionKey="interactionDescriptions:pages.adminDashboard.adminResellerDetailsPopup.button.viewCoupons"
                      />

                      <Button
                        typeButton="submit"
                        label={t("reseller_save_action")}
                        bgColor={THEME.colors.buttonBGSaveColor}
                        descriptionKey="interactionDescriptions:pages.adminDashboard.adminResellerDetailsPopup.button.save"
                      />
                    </div>
                  </form>
                </div>
              )}
            </div>
          </Animate>
        </div>
      </ModalPortal>

      {detail && isClientsPopupOpen && (
        <ResellerClientsPopup clients={detail.clients} onClose={() => setIsClientsPopupOpen(false)} />
      )}

      {detail && isCommissionsPopupOpen && (
        <ResellerCommissionStatementPopup
          entries={detail.commissionEntries}
          onMarkPaid={handleMarkPaid}
          onCreatePayment={handleCreatePayment}
          onClose={() => setIsCommissionsPopupOpen(false)}
        />
      )}

      {isCouponsPopupOpen && (
        <ResellerCouponsPopup resellerId={resellerId} onClose={() => setIsCouponsPopupOpen(false)} />
      )}
    </>
  )
}
